import { useState, type FormEvent } from "react";
import { MoreHorizontal, Pencil, Plus, Star } from "lucide-react";
import type { Contact, ContactAddress, ContactPhone } from "@/lib/types";
import type { ApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { useToast } from "@/components/Toaster";
import { mailStatus } from "./ContactHeader";
import { useContactInfo } from "./hooks";
import { AddressBlocks, MarketingDialog, useAddressBlocks } from "./MarketingEmail";

/** Emails (each with its own mail status), phones and addresses, all editable. */
export function ContactInfoCard({ c }: { c: Contact }) {
  const info = useContactInfo(c.id);
  const toast = useToast();
  const fail = (e: Error) => toast(e.message, "error");
  const [adding, setAdding] = useState<"email" | "phone" | null>(null);
  const [editPhone, setEditPhone] = useState<ContactPhone | null>(null);
  const [editAddress, setEditAddress] = useState<ContactAddress["kind"] | null>(null);
  const [confirm, setConfirm] = useState<{ what: string; run: () => void } | null>(null);
  const [marketing, setMarketing] = useState<{ email?: string } | null>(null);
  const blocks = useAddressBlocks(c.emails.map((e) => e.email));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Email, phone and address</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <section aria-label="Email addresses" className="space-y-1">
          {c.emails.map((e, i) => (
            <div key={e.id} className="space-y-0.5">
              <div className="group flex min-w-0 items-center gap-2">
              <Star
                className={i === 0 ? "size-3.5 shrink-0 fill-[var(--color-warning)] text-[var(--color-warning)]" : "size-3.5 shrink-0 text-transparent"}
                aria-label={i === 0 ? "Primary address" : undefined}
              />
              <a href={`mailto:${e.email}`} className="min-w-0 truncate hover:underline">
                {e.email}
              </a>
              {e.label && <span className="text-xs text-[var(--color-muted-foreground)]">{e.label}</span>}
              <Badge variant={mailStatus[e.status]?.variant ?? "muted"} title={mailStatus[e.status]?.help}>
                {mailStatus[e.status]?.label ?? e.status}
              </Badge>
              <RowMenu label={`Actions for ${e.email}`}>
                {i > 0 && <DropdownMenuItem onSelect={() => info.updateEmail.mutate({ itemId: e.id, make_primary: true }, { onSuccess: () => toast("Primary address changed"), onError: fail })}>Make primary</DropdownMenuItem>}
                {["work", "home", "other"].map((l) => (
                  <DropdownMenuItem key={l} disabled={e.label === l} onSelect={() => info.updateEmail.mutate({ itemId: e.id, label: l }, { onError: fail })}>
                    Label: {l}
                  </DropdownMenuItem>
                ))}
                {e.label && <DropdownMenuItem onSelect={() => info.updateEmail.mutate({ itemId: e.id, label: "" }, { onError: fail })}>Clear label</DropdownMenuItem>}
                <DropdownMenuItem onSelect={() => setMarketing({ email: e.email })}>Marketing email…</DropdownMenuItem>
                <DropdownMenuItem
                  className="text-[var(--color-destructive)]"
                  onSelect={() => setConfirm({ what: `Remove ${e.email} from this contact? Its unsubscribe and bounce history stays.`, run: () => info.removeEmail.mutate(e.id, { onSuccess: () => toast("Email address removed"), onError: fail }) })}
                >
                  Remove…
                </DropdownMenuItem>
              </RowMenu>
              </div>
              <AddressBlocks blocks={blocks(e.email)} />
            </div>
          ))}
          {adding === "email" ? (
            <InlineAdd
              placeholder="name@example.com"
              type="email"
              onCancel={() => setAdding(null)}
              onSave={(v) => info.addEmail.mutate({ email: v }, { onSuccess: () => (setAdding(null), toast("Email address added")), onError: fail })}
              pending={info.addEmail.isPending}
            />
          ) : (
            <span className="flex flex-wrap items-center gap-x-4">
              <AddLink onClick={() => setAdding("email")}>Add email</AddLink>
              {c.emails.length > 0 && (
                <button type="button" className="text-xs text-[var(--color-primary)] hover:underline" onClick={() => setMarketing({})}>
                  Marketing email…
                </button>
              )}
            </span>
          )}
          {marketing && <MarketingDialog c={c} initialEmail={marketing.email} onClose={() => setMarketing(null)} />}
        </section>

        <section aria-label="Phone numbers" className="space-y-1 border-t pt-3">
          {c.phones.map((p, i) => (
            <div key={p.id} className="flex items-center gap-2">
              <Star className={i === 0 ? "size-3.5 shrink-0 fill-[var(--color-warning)] text-[var(--color-warning)]" : "size-3.5 shrink-0 text-transparent"} aria-label={i === 0 ? "Primary number" : undefined} />
              <a href={`tel:${p.e164 ?? p.number}`} className="num hover:underline">
                {p.number}
              </a>
              {p.extension && <span className="num">ext. {p.extension}</span>}
              <span className="text-xs text-[var(--color-muted-foreground)]">{p.type}</span>
              <RowMenu label={`Actions for ${p.number}`}>
                <DropdownMenuItem onSelect={() => setEditPhone(p)}>Edit…</DropdownMenuItem>
                {i > 0 && <DropdownMenuItem onSelect={() => info.updatePhone.mutate({ itemId: p.id, make_primary: true }, { onError: fail })}>Make primary</DropdownMenuItem>}
                <DropdownMenuItem
                  className="text-[var(--color-destructive)]"
                  onSelect={() => setConfirm({ what: `Remove ${p.number} from this contact?`, run: () => info.removePhone.mutate(p.id, { onSuccess: () => toast("Phone number removed"), onError: fail }) })}
                >
                  Remove…
                </DropdownMenuItem>
              </RowMenu>
            </div>
          ))}
          {c.phones.length === 0 && adding !== "phone" && <p className="text-[var(--color-muted-foreground)]">No phone number.</p>}
          {adding === "phone" ? (
            <InlineAdd
              placeholder="(970) 555-0142"
              type="tel"
              onCancel={() => setAdding(null)}
              onSave={(v) => info.addPhone.mutate({ number: v, type: "mobile" }, { onSuccess: () => (setAdding(null), toast("Phone number added")), onError: fail })}
              pending={info.addPhone.isPending}
            />
          ) : (
            <AddLink onClick={() => setAdding("phone")}>Add phone</AddLink>
          )}
        </section>

        <section aria-label="Addresses" className="grid gap-3 border-t pt-3 sm:grid-cols-3">
          {(["billing", "shipping", "other"] as const).map((kind) => {
            const a = c.addresses.find((x) => x.kind === kind);
            return (
              <div key={kind} className="min-w-0">
                <div className="flex items-center gap-1 text-xs text-[var(--color-muted-foreground)]">
                  {kind === "billing" ? "Billing address" : kind === "shipping" ? "Shipping address" : "Other address"}
                  <button className="rounded p-0.5 hover:bg-[var(--color-muted)]" aria-label={`Edit ${kind} address`} onClick={() => setEditAddress(kind)}>
                    <Pencil className="size-3" />
                  </button>
                </div>
                {a ? (
                  <address className="not-italic leading-snug">
                    {a.line1 && <div>{a.line1}</div>}
                    {a.line2 && <div>{a.line2}</div>}
                    <div>{[a.city, [a.region, a.postal_code].filter(Boolean).join(" ")].filter(Boolean).join(", ")}</div>
                    {a.country_code && a.country_code !== "US" && <div>{a.country_code}</div>}
                  </address>
                ) : (
                  <span className="text-[var(--color-muted-foreground)]">—</span>
                )}
              </div>
            );
          })}
        </section>
      </CardContent>

      {editPhone && <PhoneDialog c={c} phone={editPhone} onClose={() => setEditPhone(null)} />}
      {editAddress && <AddressDialog c={c} kind={editAddress} onClose={() => setEditAddress(null)} />}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent>
          <DialogTitle>Are you sure?</DialogTitle>
          <DialogDescription>{confirm?.what}</DialogDescription>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirm(null)}>
              Keep it
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                confirm?.run();
                setConfirm(null);
              }}
            >
              Remove
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function RowMenu({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="ml-auto rounded p-0.5 text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]" aria-label={label}>
        <MoreHorizontal className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">{children}</DropdownMenuContent>
    </DropdownMenu>
  );
}

function AddLink({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline">
      <Plus className="size-3" /> {children}
    </button>
  );
}

function InlineAdd({ placeholder, type, onSave, onCancel, pending }: { placeholder: string; type: string; onSave: (v: string) => void; onCancel: () => void; pending: boolean }) {
  const [v, setV] = useState("");
  return (
    <form
      className="flex gap-1.5"
      onSubmit={(e) => {
        e.preventDefault();
        if (v.trim()) onSave(v.trim());
      }}
    >
      <Input autoFocus type={type} className="h-8" value={v} onChange={(e) => setV(e.target.value)} placeholder={placeholder} onKeyDown={(e) => e.key === "Escape" && onCancel()} />
      <Button size="sm" type="submit" disabled={pending || !v.trim()}>
        Add
      </Button>
      <Button size="sm" variant="ghost" type="button" onClick={onCancel}>
        Cancel
      </Button>
    </form>
  );
}

function PhoneDialog({ c, phone, onClose }: { c: Contact; phone: ContactPhone; onClose: () => void }) {
  const info = useContactInfo(c.id);
  const [number, setNumber] = useState(phone.number);
  const [type, setType] = useState(phone.type);
  const [ext, setExt] = useState(phone.extension ?? "");
  const save = (e: FormEvent) => {
    e.preventDefault();
    info.updatePhone.mutate({ itemId: phone.id, number, type, extension: ext }, { onSuccess: onClose });
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>Edit phone number</DialogTitle>
        <DialogDescription>Changes are saved to the contact and recorded in the audit log.</DialogDescription>
        <form className="grid grid-cols-[1fr_8rem_6rem] gap-2" onSubmit={save}>
          <div className="space-y-1">
            <Label htmlFor="ph-num">Number</Label>
            <Input id="ph-num" autoFocus value={number} onChange={(e) => setNumber(e.target.value)} required />
          </div>
          <div className="space-y-1">
            <Label htmlFor="ph-type">Type</Label>
            <Select id="ph-type" value={type} onChange={(e) => setType(e.target.value)}>
              {["mobile", "work", "home", "other"].map((t) => (
                <option key={t}>{t}</option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ph-ext">Extension</Label>
            <Input id="ph-ext" value={ext} onChange={(e) => setExt(e.target.value)} />
          </div>
          {info.updatePhone.error && <p className="col-span-3 text-sm text-[var(--color-destructive)]">{(info.updatePhone.error as ApiError).message}</p>}
          <div className="col-span-3 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={info.updatePhone.isPending}>
              Save phone
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddressDialog({ c, kind, onClose }: { c: Contact; kind: ContactAddress["kind"]; onClose: () => void }) {
  const info = useContactInfo(c.id);
  const a = c.addresses.find((x) => x.kind === kind);
  const [v, setV] = useState({
    line1: a?.line1 ?? "",
    line2: a?.line2 ?? "",
    city: a?.city ?? "",
    region: a?.region ?? "",
    postal_code: a?.postal_code ?? "",
    country_code: a?.country_code ?? "US",
  });
  const field = (k: keyof typeof v, label: string, cls = "") => (
    <div className={`space-y-1 ${cls}`}>
      <Label htmlFor={`addr-${k}`}>{label}</Label>
      <Input id={`addr-${k}`} value={v[k]} onChange={(e) => setV({ ...v, [k]: k === "country_code" ? e.target.value.toUpperCase().slice(0, 2) : e.target.value })} />
    </div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle className="capitalize">{kind} address</DialogTitle>
        <DialogDescription>Replaces the {kind} address. Leave every line empty to clear it.</DialogDescription>
        <form
          className="grid grid-cols-6 gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            info.setAddress.mutate({ kind, ...v }, { onSuccess: onClose });
          }}
        >
          {field("line1", "Street", "col-span-6")}
          {field("line2", "Apartment, suite (optional)", "col-span-6")}
          {field("city", "City", "col-span-3")}
          {field("region", "State", "col-span-1")}
          {field("postal_code", "ZIP", "col-span-2")}
          {field("country_code", "Country (2 letters)", "col-span-2")}
          {info.setAddress.error && <p className="col-span-6 text-sm text-[var(--color-destructive)]">{(info.setAddress.error as ApiError).message}</p>}
          <div className="col-span-6 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={info.setAddress.isPending}>
              Save address
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
